/**
 * /field/downhole/work/:id — one work order, worked from the shop floor
 * (fieldiq-api#905 `PATCH /work-orders/:id`).
 *
 * WHAT A HAND CAN DO, on an order assigned to them:
 *   Start              → in_progress
 *   Waiting on parts   → awaiting_parts
 *   Complete           findings, resolution, labour hours; an inspection-type
 *                      order also needs PASS or FAIL (the API refuses without
 *                      it) and may set the next inspection due.
 * The buttons follow the API's transition map, so none is refused for its
 * status. Cancelling, re-prioritising, reassigning and the parts list (it
 * carries a cost) are office work and are not here.
 *
 * DOPE & CLEANING. When the last open order on a tool closes, the API returns
 * the tool to Available only behind dope & cleaning — confirmed already, or
 * confirmed with this change. The screen cannot see whether this is the last
 * open order, so the box is offered on completion and its 409 is shown as it
 * comes back, with the box to tick. A FAIL sends the tool to Failed instead.
 *
 * An order assigned to someone else, or to nobody, is shown read-only: the
 * office decides who works it.
 */
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, Loader2, PackageSearch, Play } from 'lucide-react';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../../api/client';
import { useAuth } from '../../../contexts/AuthContext';
import { useDownholeWorkOrder, useUpdateWorkOrder } from '../../../hooks/useDownhole';
import { formatInstant, formatInstantTenantDay } from '../../../lib/calendar-date';
import { fmtHours } from '../../../lib/display-labels';
import { onlineOrSay } from '../../../lib/downhole-offline';
import { canBuildDownhole } from '../../../lib/downhole-roles';
import {
  DH_CATEGORY_LABEL,
  DH_PRIORITY_LABEL,
  DH_WORK_ORDER_STATUS_SKIN,
  DH_WORK_TYPE_LABEL,
  dhLabel,
} from '../../../lib/downhole-labels';
import { DH_INSPECTION_WORK_TYPES, DH_WORK_ORDER_TRANSITIONS, type DhWorkOrder, type DhWorkOrderWrite } from '../../../types/downhole';
import { DH_DATE_INPUT, DH_INPUT, DH_SECONDARY, DhBadge, DhDialog, DhField, DhSpec } from '../DownholeBits';
import { conflictCode } from '../WorkOrderDialogs';
import { FIELD_PRIMARY, FIELD_SECONDARY, FieldCard, FieldEmpty, FieldQueryState, FieldTitle } from './FieldBits';

const HOURS = /^\d+(\.\d+)?$/;
const DAY = /^\d{4}-\d{2}-\d{2}$/;

export default function FieldWorkOrderScreen() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const query = useDownholeWorkOrder(id);
  const update = useUpdateWorkOrder();
  const [completing, setCompleting] = useState(false);
  const wo = query.data?.data ?? null;
  const tz = query.data?.meta.time_zone ?? null;

  const mine = !!wo && !!user?.id && wo.assigned_to === user.id && canBuildDownhole(user.role);
  const allowed = wo ? DH_WORK_ORDER_TRANSITIONS[wo.status] ?? [] : [];

  const move = async (status: string, label: string) => {
    if (!wo || !onlineOrSay(toast.error)) return;
    try {
      await update.mutateAsync({ id: wo.id, body: { status } });
      toast.success(label);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'The work order was not updated.'));
    }
  };

  return (
    <div data-testid="downhole-field-work-order">
      <FieldQueryState query={query} subject="this work order">
        {!wo ? (
          <FieldEmpty>This work order does not exist in your company.</FieldEmpty>
        ) : (
          <>
            <FieldTitle eyebrow={wo.wo_number ?? 'Work order'} title={`${dhLabel(DH_WORK_TYPE_LABEL, wo.work_type)} · ${wo.component_serial ?? '—'}`}>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <DhBadge map={DH_WORK_ORDER_STATUS_SKIN} value={wo.status} />
                <span className="text-xs text-fieldiq-text">{dhLabel(DH_PRIORITY_LABEL, wo.priority)} priority</span>
              </div>
            </FieldTitle>

            {mine && allowed.length > 0 && (
              <div className="space-y-2">
                {allowed.includes('completed') && (
                  <button type="button" className={FIELD_PRIMARY} disabled={update.isPending} onClick={() => setCompleting(true)}>
                    <CheckCircle2 className="size-4" aria-hidden="true" />
                    Complete
                  </button>
                )}
                {allowed.includes('in_progress') && (
                  <button type="button" className={FIELD_SECONDARY} disabled={update.isPending} onClick={() => move('in_progress', 'Started')}>
                    <Play className="size-4" aria-hidden="true" />
                    {wo.status === 'awaiting_parts' ? 'Parts are here — resume' : 'Start'}
                  </button>
                )}
                {allowed.includes('awaiting_parts') && (
                  <button type="button" className={FIELD_SECONDARY} disabled={update.isPending} onClick={() => move('awaiting_parts', 'Marked waiting on parts')}>
                    <PackageSearch className="size-4" aria-hidden="true" />
                    Waiting on parts
                  </button>
                )}
              </div>
            )}
            {!mine && allowed.length > 0 && (
              <p className="text-sm text-fieldiq-text">
                {wo.assigned_to_name ? `Assigned to ${wo.assigned_to_name}.` : 'Not assigned to anyone yet.'} The office decides who works it.
              </p>
            )}

            <FieldCard className="mt-4">
              <dl className="grid grid-cols-2 gap-x-4 gap-y-4 p-4">
                <DhSpec label="Tool" value={<span className="font-mono">{wo.component_serial ?? '—'}</span>} />
                <DhSpec label="Category" value={dhLabel(DH_CATEGORY_LABEL, wo.component_category)} />
                {/* due_date is a calendar day, not an instant. */}
                <DhSpec label="Due" value={formatInstantTenantDay(wo.due_date, tz) ?? '—'} />
                <DhSpec label="Started" value={formatInstant(wo.started_at, tz) ?? '—'} />
                <DhSpec label="Labour" value={fmtHours(wo.labor_hours, 1)} />
              </dl>
              <div className="space-y-3 border-t border-fieldiq-border p-4 text-sm">
                <div>
                  <p className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">What to do</p>
                  <p className="mt-1 whitespace-pre-line text-fieldiq-text">{wo.description || '—'}</p>
                </div>
                {wo.findings && (
                  <div>
                    <p className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Findings</p>
                    <p className="mt-1 whitespace-pre-line text-fieldiq-text">{wo.findings}</p>
                  </div>
                )}
                {wo.resolution && (
                  <div>
                    <p className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Resolution</p>
                    <p className="mt-1 whitespace-pre-line text-fieldiq-text">{wo.resolution}</p>
                  </div>
                )}
              </div>
            </FieldCard>

            {mine && <CompleteWorkOrderDialog open={completing} onClose={() => setCompleting(false)} wo={wo} />}
          </>
        )}
      </FieldQueryState>
    </div>
  );
}

/**
 * Completing an order — shared with the office's Work Orders screen, so a
 * completion sends the same body from the bench and the office.
 */
export function CompleteWorkOrderDialog({ open, onClose, wo }: { open: boolean; onClose: () => void; wo: DhWorkOrder }) {
  const update = useUpdateWorkOrder();
  const inspection = (DH_INSPECTION_WORK_TYPES as readonly string[]).includes(wo.work_type ?? '');
  const [findings, setFindings] = useState(wo.findings ?? '');
  const [resolution, setResolution] = useState(wo.resolution ?? '');
  const [labor, setLabor] = useState('');
  const [result, setResult] = useState<'' | 'pass' | 'fail'>('');
  const [nextDue, setNextDue] = useState('');
  const [dope, setDope] = useState(false);
  const [needsDope, setNeedsDope] = useState(false);

  const submit = async () => {
    const problems: string[] = [];
    if (inspection && !result) problems.push('Choose pass or fail');
    if (labor.trim() && !HOURS.test(labor.trim())) problems.push('Labour hours must be a number');
    if (nextDue && !DAY.test(nextDue)) problems.push('Next inspection due must be a date');
    if (problems.length) {
      toast.error(problems.join('. '));
      return;
    }
    if (!onlineOrSay(toast.error)) return;
    const body: DhWorkOrderWrite = { status: 'completed' };
    if (findings.trim() !== (wo.findings ?? '')) body.findings = findings.trim() || null;
    if (resolution.trim() !== (wo.resolution ?? '')) body.resolution = resolution.trim() || null;
    if (labor.trim()) body.labor_hours = Number(labor.trim());
    if (inspection) body.inspection_result = result;
    if (inspection && nextDue) body.next_inspection_due = nextDue;
    if (dope) body.dope_clean_confirmed = true;
    try {
      await update.mutateAsync({ id: wo.id, body });
      toast.success(`${wo.wo_number ?? 'Work order'} completed`);
      onClose();
    } catch (error) {
      // By the API's machine code (api#913), not the sentence.
      if (conflictCode(error) === 'DOPE_CLEAN_UNCONFIRMED') setNeedsDope(true);
      toast.error(getApiErrorMessage(error, 'The work order was not completed.'));
    }
  };

  return (
    <DhDialog
      open={open}
      title={`Complete ${wo.wo_number ?? 'work order'}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={DH_SECONDARY} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="fi-action fi-action--standard fi-action--primary disabled:opacity-50" disabled={update.isPending} onClick={submit}>
            {update.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Complete
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {inspection && (
          <fieldset>
            <legend className="mb-2 text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Result *</legend>
            <div className="grid grid-cols-2 gap-2">
              {(['pass', 'fail'] as const).map((r) => (
                <label
                  key={r}
                  className={`flex h-12 items-center justify-center gap-2 rounded-fi-control border text-sm font-bold ${
                    result === r ? 'border-fi-amber bg-fi-amber/10 text-fieldiq-text' : 'border-fieldiq-border text-fieldiq-text'
                  }`}
                >
                  <input type="radio" name="inspection-result" checked={result === r} onChange={() => setResult(r)} />
                  {r === 'pass' ? 'Pass' : 'Fail'}
                </label>
              ))}
            </div>
            {result === 'fail' && <p className="mt-2 text-xs text-fieldiq-text">A fail sends the tool to Failed, for a repair order.</p>}
          </fieldset>
        )}
        <DhField label="Findings">
          <textarea className={`${DH_INPUT} h-20 py-2`} value={findings} onChange={(e) => setFindings(e.target.value)} maxLength={10000} />
        </DhField>
        <DhField label="Resolution">
          <textarea className={`${DH_INPUT} h-20 py-2`} value={resolution} onChange={(e) => setResolution(e.target.value)} maxLength={10000} />
        </DhField>
        <div className="grid grid-cols-2 gap-3">
          <DhField label="Labour (h)">
            <input className={DH_INPUT} inputMode="decimal" value={labor} onChange={(e) => setLabor(e.target.value)} />
          </DhField>
          {inspection && (
            <DhField label="Next inspection due">
              <input type="date" className={DH_DATE_INPUT} value={nextDue} onChange={(e) => setNextDue(e.target.value)} />
            </DhField>
          )}
        </div>
        {result !== 'fail' && (
          <label className={`flex min-h-[2.75rem] items-center gap-3 text-sm text-fieldiq-text ${needsDope ? 'font-bold' : ''}`}>
            <input type="checkbox" className="size-5" checked={dope} onChange={(e) => setDope(e.target.checked)} />
            Dope &amp; cleaning done{needsDope ? ' — required before the tool goes back to stock' : ' (needed if this tool goes back to stock)'}
          </label>
        )}
      </div>
    </DhDialog>
  );
}
